import { Injectable } from '@angular/core';
import { InstanceDeserializer, StudioDataInstance } from '@gametailors/lp-studio-connector';
import { Err, Ok, Result } from '@gametailors/web-core/classes';
import { LpSchema } from '../../../typings/schema';
import { STUDIO_API_DATA_PACKAGE_PROVIDER } from '../../leerplus/rid-game.provider';

type LpInstance = LpSchema['all'];

interface LoadedPackage {
  deserializer: InstanceDeserializer<LpInstance>;
  instances: StudioDataInstance[];
}

function isStudioDataInstance(value: unknown): value is StudioDataInstance {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<StudioDataInstance>;
  return typeof candidate.uid === 'string' && typeof candidate.dataType === 'string' && Array.isArray(candidate.fieldValues);
}

function describe(value: unknown): string {
  return (JSON.stringify(value) ?? String(value)).slice(0, 200);
}

/**
 * Turns the raw data instance graph a generator returns into the Lp object it describes. References to content that
 * lives in the data package (a word, say) are resolved against the package, which is loaded once.
 */
@Injectable({ providedIn: 'root' })
export class GeneratedContentDeserializer {
  private readonly dataPackageProvider = STUDIO_API_DATA_PACKAGE_PROVIDER;
  private loadedPackage?: Promise<LoadedPackage>;

  public async deserialize<T extends LpInstance>(output: unknown, dataType: T['dataType']): Promise<Result<T, string>> {
    if (!isStudioDataInstance(output)) {
      return Err(`expected a data instance, got ${describe(output)}`);
    }
    if (output.dataType !== dataType) {
      return Err(`expected a ${dataType}, got a ${output.dataType}`);
    }

    try {
      const { deserializer, instances } = await this.loadPackage();
      return Ok(deserializer.deserialize(output, instances) as T);
    } catch (error) {
      return Err(error instanceof Error ? error.message : String(error));
    }
  }

  /** Every instance of the data package with the given data type. */
  public async getAll<T extends LpInstance>(dataType: T['dataType']): Promise<T[]> {
    const { deserializer, instances } = await this.loadPackage();
    // all of them, so references to other instances resolve
    return deserializer.deserializeMany(instances).filter((instance): instance is T => instance.dataType === dataType);
  }

  private loadPackage(): Promise<LoadedPackage> {
    this.loadedPackage ??= this.fetchPackage().catch((error: unknown) => {
      // not cached, so the next call tries again
      this.loadedPackage = undefined;
      throw error;
    });
    return this.loadedPackage;
  }

  private async fetchPackage(): Promise<LoadedPackage> {
    const [schema, files, dataPackage] = await Promise.all([
      this.dataPackageProvider.getSchema(),
      this.dataPackageProvider.getFileMetas(),
      this.dataPackageProvider.getDataPackage(),
    ]);
    return {
      deserializer: new InstanceDeserializer<LpInstance>(schema, files, dataPackage.variables),
      instances: dataPackage.instances,
    };
  }
}
